'use client';

import { useState } from 'react';
import { useLang } from '@/i18n/client';
import { track } from '@/lib/ev-client';

const COPY = {
  en: { failed: 'That did not work. Please try again.', done: <>You&apos;re in. Here is your code for 10% off your first order:</>, copied: 'Copied', copy: 'Copy', saved: <>It&apos;s already saved for checkout.</>, email: 'Email address' },
  de: { failed: 'Das hat nicht geklappt. Bitte versuch es noch einmal.', done: <>Du bist dabei. Hier ist dein Code für 10% Rabatt auf deine erste Bestellung:</>, copied: 'Kopiert', copy: 'Kopieren', saved: <>Er ist für die Kasse schon gespeichert.</>, email: 'E-Mail-Adresse' },
  fr: { failed: 'Ça n’a pas fonctionné. Réessaie.', done: <>Tu es dans la liste. Voici ton code pour 10 % sur ta première commande :</>, copied: 'Copié', copy: 'Copier', saved: <>Il est déjà enregistré pour le paiement.</>, email: 'Adresse e-mail' },
};

type Props = { id: string; className?: string; button: string; placeholder: string; goal?: string; children?: React.ReactNode; onDone?: () => void };

export function SubscribeForm({ id, className, button, placeholder, goal, children, onDone }: Props) {
  const t = COPY[useLang()];
  const [state, setState] = useState<{ status: 'idle' | 'busy' | 'done' | 'error'; message?: string; code?: string }>({ status: 'idle' });
  const [copied, setCopied] = useState(false);

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const email = new FormData(e.currentTarget).get('email');
    setState({ status: 'busy' });
    try {
      const res = await fetch('/api/subscribe', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, goal }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t.failed);
      setState({ status: 'done', code: data.code });
      track('subscribe', { form: id, goal });
      try { localStorage.setItem('batanapro.subscribed', '1'); localStorage.setItem('batanapro.promo', data.code); } catch {}
      onDone?.();
    } catch (err) {
      setState({ status: 'error', message: (err as Error).message });
    }
  }

  if (state.status === 'done') {
    return (
      <div className={className}>
        <p className="form-msg" role="status">{t.done}</p>
        <div className="code-reveal">
          <code>{state.code}</code>
          <button type="button" className="btn btn--ghost" style={{ minHeight: 38, padding: '0 14px' }} onClick={async () => { try { await navigator.clipboard.writeText(state.code!); setCopied(true); } catch {} }}>{copied ? t.copied : t.copy}</button>
        </div>
        <small>{t.saved}</small>
      </div>
    );
  }
  return (
    <form className={className} onSubmit={submit} noValidate>
      <label className="sr-only" htmlFor={id}>{t.email}</label>
      <div className="field-row">
        <input id={id} name="email" type="email" autoComplete="email" placeholder={placeholder} required />
        <button className="btn" type="submit" disabled={state.status === 'busy'}>{button}</button>
      </div>
      <p className={`form-msg${state.status === 'error' ? ' is-error' : ''}`} role="status">{state.message}</p>
      {children}
    </form>
  );
}
